import { Injectable, inject } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, retry, tap, timeout } from "rxjs/operators";
import {
  CloudProvider,
  FilterableL1,
  FilterableL2,
  FilterOperator,
  FiltersType,
  GroupByPayload,
  QueryFilterSimple,
  QueryMetadata,
  TableId,
} from "../models/vcloud-api";
import { Filter, FilterValue } from "../models/filtering";
import { ApplyShareLinkFiltersParams } from "../models/report-sharing";
import { LoggerService } from "./logger.service";
import { AlertsService } from "./alerts.service";
import { FormattingService } from "./formatting.service";
import {
  AZURE_FILTER_DEFINITIONS,
  FILTERS_API_ENDPOINTS,
  FILTERS_API_TIMEOUTS,
  FILTERS_STORAGE_KEYS,
  FILTER_OPERATORS_DEFAULT,
} from "./service.constants";
import { OPERATOR_SCORES } from "../constant/vcloud-api";
import { VcloudHttpClientService } from "./vcloud-http-client.service";
import { environment } from "../../../environments/environment";
import { VcloudCostTrackerService } from "./vcloud-cost-tracker.service";
import { VcloudDebugCaptureService } from "./vcloud-debug-capture.service";

@Injectable({
  providedIn: "root",
})
export class VcloudFiltersApiService {
  private formatting: FormattingService = inject(FormattingService);
  private alerts: AlertsService = inject(AlertsService);
  private log: LoggerService = inject(LoggerService);
  private httpClient: VcloudHttpClientService = inject(VcloudHttpClientService);
  private costTracker: VcloudCostTrackerService = inject(VcloudCostTrackerService);
  private debugCapture: VcloudDebugCaptureService = inject(VcloudDebugCaptureService);
  private handleSyncError(context: string, error: unknown): void {
    this.log.error({ msg: context, error });
  }

  private handleApiError(context: string, error: unknown): Observable<never> {
    this.log.error({ msg: context, error });
    return throwError(() => error);
  }

  public getAzureFilters(
    addMonths: (_date: number | string | Date, months: number, dayNumber?: number) => Date,
  ): Filter[] {
    return [
      {
        displayName: "Select Date Range",
        id: "Date",
        selected: new Set<string>(),
        table: "Table_1",
        operators: FILTER_OPERATORS_DEFAULT.DATE_RANGE as FilterOperator[],
        values: [
          addMonths(new Date(), -4).toISOString().substring(0, 10),
          addMonths(new Date(), 0, 0).toISOString().substring(0, 10),
        ],
        endOfDateRange: "exclusive",
        type: FiltersType.date,
        query: "",
        datePickerRange: true,
      } as Filter,
    ].concat(
      AZURE_FILTER_DEFINITIONS.map(
        ({ displayName, id, table, type }) =>
          ({
            displayName,
            id,
            selected: new Set(),
            values: [],
            table,
            type,
            query: "",
          }) as Filter,
      ),
    );
  }

  public preProcessRequestFilters(allFilters: (QueryFilterSimple | FilterableL2)[]): QueryFilterSimple[] {
    const simplifiedFilters: (QueryFilterSimple | FilterableL1)[] = allFilters
      .filter((f) => (f as FilterableL2).Filters === undefined)
      .map((f) => this.simplifyFilter(f as QueryFilterSimple));

    const deduplicatedSimplifedFilters: (QueryFilterSimple | FilterableL1)[] =
      this.deduplicateFilters(simplifiedFilters);

    const orderedSimpleFilters: (QueryFilterSimple | FilterableL1)[] = deduplicatedSimplifedFilters
      .filter(
        (f) =>
          (f as QueryFilterSimple).Type === FiltersType.date || (f as QueryFilterSimple).Type === FiltersType.timestamp,
      )
      .concat(
        this.orderFilters(
          deduplicatedSimplifedFilters.filter(
            (f) =>
              (f as QueryFilterSimple).Type !== FiltersType.date &&
              (f as QueryFilterSimple).Type !== FiltersType.timestamp,
          ) as QueryFilterSimple[],
        ),
      );

    const complexFilters: (QueryFilterSimple | FilterableL1)[] = allFilters.filter(
      (f) => (f as FilterableL2).Filters !== undefined,
    );

    return orderedSimpleFilters.concat(complexFilters) as QueryFilterSimple[];
  }

  public applyShareLinkFilters(share: ApplyShareLinkFiltersParams, mergeFilters: Filter[]): void {
    for (const f of share?.Filters?.all ?? []) {
      try {
        const filterId: string = (f?.any ?? f?.all).find(({ eq: [key] }) => !!key).eq[0];
        const filter: Filter = mergeFilters.find((filterItem: Filter): boolean => {
          return (
            this.formatting.parseAttribute(filterItem.id).attribute ===
            this.formatting.parseAttribute(filterId).attribute
          );
        });
        if (filter) {
          this.log.info({ msg: "matched filter", filter, filterId });
        }
        if (filter?.datePickerRange && Array.isArray(f.all)) {
          filter.values = f.all.map((v) => v.eq[1]);
          this.log.info({ msg: "date filter", filter, filterId });
        } else if (filter?.selected !== undefined) {
          filter.selected.clear();
          let sortRequired: boolean = false;
          for (const v of f.any ?? []) {
            filter.selected.add(v.eq[1]);
            if (!filter.values.includes(v.eq[1])) {
              filter.values.push(v.eq[1]);
              sortRequired = true;
            }
          }
          if (sortRequired) {
            filter.values.sort();
          }
          this.log.info({ msg: "value filter", filter, filterId, sortRequired });
        }
      } catch (e) {
        this.handleSyncError("applyShareLinkFilters", e);
      }
    }
  }

  public extractFiltersFromShareId(shareId: string): {
    Filters: { all: Array<{ any: Array<{ eq: [string, string] }> }> };
  } {
    const o: {
      Filters: { all: Array<{ any: Array<{ eq: [string, string] }> }> };
    } = { Filters: { all: [] } };
    const filters: { [filter: string]: Array<string> } = JSON.parse(shareId);
    for (const k in filters) {
      const v: string[] = filters[k];
      o.Filters.all.push({ any: v.map((value) => ({ eq: [k, value] })) });
    }
    return o;
  }
  public convertToBqFilters(
    filters: Filter[],
    isRecallingFilters: boolean = false,
    isSharedReport: boolean = false,
  ): QueryFilterSimple[] {
    const nonDateFilters: QueryFilterSimple[] = filters
      .filter(({ datePickerRange }) => !datePickerRange)
      .filter(({ selected }) => (selected?.size ?? 0) > 0)
      .filter(({ selected, values }) => {
        if (isRecallingFilters || isSharedReport) {
          return true;
        }
        return (selected?.size ?? 0) !== (values?.length ?? 0);
      })
      .map(({ id, selected, table, type }: Filter): QueryFilterSimple => {
        const values: Array<FilterValue> = Array.from(selected || []);
        if (type === FiltersType.float64) {
          for (let i = 0; i < values.length; i++) {
            if (typeof values[i] === "string") {
              values[i] = Number.parseFloat(values[i] as string);
            }
          }
        } else if (type === FiltersType.int64) {
          for (let i = 0; i < values.length; i++) {
            if (typeof values[i] === "string") {
              values[i] = Number.parseInt(values[i] as string, 10);
            }
          }
        } else if (type === FiltersType.bool) {
          for (let i = 0; i < values.length; i++) {
            if (typeof values[i] === "string") {
              values[i] = (values[i] as string).toLowerCase().trim() === "true";
            }
          }
        }
        return {
          Table: table,
          Type: type,
          Attribute: id,
          Operator: "IN" as FilterOperator,
          Value: values,
        };
      });

    const dateFilters: QueryFilterSimple[] = filters
      .filter(({ datePickerRange }) => datePickerRange)
      .map(({ id, operators, monthYearMode, table, type, values: [startDate, endDate] }) => {
        let startOperator: FilterOperator = ">=";
        let endOperator: FilterOperator = "<=";
        if (operators) {
          startOperator = operators[0] ?? ">=";
          endOperator = operators[1] ?? "<=";
        }
        if (startDate === endDate && (monthYearMode || (startOperator === ">=" && endOperator === "<="))) {
          return [
            {
              Table: table,
              Type: type,
              Attribute: id,
              Operator: "=" as FilterOperator,
              Value: startDate,
            } as QueryFilterSimple,
          ];
        }
        return [
          {
            Table: table,
            Type: type,
            Attribute: id,
            Operator: startOperator as FilterOperator,
            Value: startDate,
          } as QueryFilterSimple,
          {
            Table: table,
            Type: type,
            Attribute: id,
            Operator: endOperator as FilterOperator,
            Value: endDate,
          } as QueryFilterSimple,
        ];
      })
      .flat();
    return nonDateFilters.concat(dateFilters);
  }
  public groupBy<T extends number | string>(
    body: GroupByPayload,
  ): Observable<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }> {
    let o: Observable<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }>;

    const requestBody: GroupByPayload = JSON.parse(this.JSONStringifyStable(body));

    if (this.standardiseRequestBody(requestBody)) {
      return of({ Results: [] as { Group: string[]; Value: T[] }[] });
    }
    this.preProcessFilters(requestBody);

    o = this.httpClient.post(FILTERS_API_ENDPOINTS.GROUP_BY, requestBody);

    return o.pipe(
      timeout(30 * 1000),
      retry(2),
      tap(({ Results }) => {
        this.preProcessResponseBody<T>(Results);
      }),
      tap(({ Metadata }) => {
        if (!environment.production) {
          setTimeout(() => {
            this.costTracker.handleQueryCost({ Metadata: { job_id: `bq-${Metadata.job_id}`, cost: Metadata.cost } });
          }, FILTERS_API_TIMEOUTS.COST_TRACKER_DELAY);
        }
      }),
      tap((response) =>
        this.debugCapture.captureResponse(response, body, (payload) => this.JSONStringifyStable(payload)),
      ),
      catchError((error) => this.handleApiError("groupBy", error)),
    );
  }
  private preProcessResponseBody<T>(Results: { Group: string[]; Value: T[] }[]) {
    const resultsSafe: {
      Group: string[];
      Value: T[];
    }[] = Results ?? [];
    for (const r of resultsSafe) {
      for (let i = 0; i < (r?.Value?.length ?? 0); i++) {
        // @ts-ignore
        if (r?.Value[i] === -0) {
          // @ts-ignore
          r.Value[i] = 0;
        }
      }
      for (let i = 0; i < (r?.Group?.length ?? 0); i++) {
        // @ts-ignore
        if (r?.Group[i] === -0) {
          // @ts-ignore
          r.Group[i] = 0;
        }
      }
    }
  }
  public JSONStringifyStable(obj: GroupByPayload): string {
    const allKeys: Set<string> = new Set<string>();
    obj = this.sortFilters(obj);

    JSON.stringify(obj, (key, value) => (allKeys.add(key), value));
    return JSON.stringify(obj, Array.from(allKeys).sort());
  }
  public standardiseRequestBody(requestBody: GroupByPayload): boolean {
    if (requestBody.OrderBy?.length === 0) {
      delete requestBody.OrderBy;
    }

    // no need to run the request
    if (requestBody.Limit === 0) {
      return true;
    }

    // default value
    if (requestBody.SelectGroups === true) {
      delete requestBody.SelectGroups;
    }

    // default value
    if (requestBody.SortOrder === "DESC") {
      delete requestBody.SortOrder;
    }

    // default value
    if (requestBody.Offset === 0) {
      delete requestBody.Offset;
    }

    return false;
  }

  public preProcessFilters(requestBody: GroupByPayload) {
    requestBody.Filters = this.preProcessRequestFilters(requestBody?.Filters ?? []);
  }

  private valuesEqual<T extends number | boolean | string, K extends number | boolean | string>(
    value1: T | T[],
    value2: K | K[],
  ): boolean {
    if (!Array.isArray(value1) || !Array.isArray(value2)) {
      return value1 === (value2 as unknown as T);
    }
    if (value1.length !== value2.length) {
      return false;
    }
    const s2: Set<K> = new Set(value2);
    for (const v1 of value1) {
      if (!s2.has(v1 as unknown as K)) {
        return false;
      }
    }
    const s1: Set<T> = new Set(value1);
    for (const v2 of value2) {
      if (!s1.has(v2 as unknown as T)) {
        return false;
      }
    }
    return true;
  }

  private filtersEqual(newFilter: QueryFilterSimple, existingFilter: QueryFilterSimple): boolean {
    return (
      existingFilter.Attribute === newFilter.Attribute &&
      existingFilter.Table === newFilter.Table &&
      existingFilter.Operator === newFilter.Operator &&
      existingFilter.Type === newFilter.Type &&
      this.valuesEqual(existingFilter.Value, newFilter.Value)
    );
  }
  public deduplicateFilters(filters: (QueryFilterSimple | FilterableL1)[]): (QueryFilterSimple | FilterableL1)[] {
    if ((filters as FilterableL1).Filters !== undefined) {
      return filters;
    }
    const newFilters: QueryFilterSimple[] = [];
    for (const newFilter of filters) {
      if (!newFilters.some((existingFilter) => this.filtersEqual(newFilter as QueryFilterSimple, existingFilter))) {
        newFilters.push(newFilter as QueryFilterSimple);
      }
    }
    return newFilters;
  }

  public openSnackBar(_message: string): void {
    this.alerts.addAlert({
      text: "Recalled previously applied filters",
      type: "success",
      dismissOnTimeout: FILTERS_API_TIMEOUTS.ALERT_DISMISS_TIMEOUT,
      dismissible: true,
    });
  }

  public setEquals<T>(v1: Set<T>, v2: Set<T>): boolean {
    for (const v of v1) {
      if (!v2.has(v)) {
        return false;
      }
    }
    for (const v of v2) {
      if (!v1.has(v)) {
        return false;
      }
    }
    return v1.size === v2.size;
  }

  public getTableForQueryFilter(f: QueryFilterSimple): { table: TableId | undefined; id: string } {
    if (f.Table) {
      return { table: f.Table, id: f.Attribute };
    }
    if (f.Attribute.startsWith("Table_1.") || f.Attribute.startsWith("Table_2.")) {
      return { table: f.Attribute.split(".")[0] as TableId, id: f.Attribute.split(".").slice(1).join(".") };
    }
    return { table: undefined, id: f.Attribute };
  }

  public getTableForFilter(f: Filter): { table: TableId | undefined; id: string } {
    if (f.table) {
      return { table: f.table, id: f.id };
    }
    if (f.id.startsWith("Table_1.") || f.id.startsWith("Table_2.")) {
      return { table: f.id.split(".")[0] as TableId, id: f.id.split(".").slice(1).join(".") };
    }
    return { table: undefined, id: f.id };
  }

  public tryRecallPreviousFilters(csp: CloudProvider): QueryFilterSimple[] {
    const getFiltersFromStorage: string = localStorage.getItem(`${FILTERS_STORAGE_KEYS.PREVIOUS_FILTERS_PREFIX}${csp}`);
    if (getFiltersFromStorage) {
      try {
        const filters: QueryFilterSimple[] = JSON.parse(getFiltersFromStorage);
        if (Array.isArray(filters)) {
          return filters;
        }
        localStorage.removeItem(`${FILTERS_STORAGE_KEYS.PREVIOUS_FILTERS_PREFIX}${csp}`);
      } catch (e) {
        this.handleSyncError("tryRecallPreviousFilters", e);
      }
    }
    return [];
  }

  public simplifyFilter(filter: QueryFilterSimple): QueryFilterSimple | FilterableL1 {
    try {
      if (Array.isArray(filter?.Value)) {
        if ((filter?.Value?.length ?? 0) === 1) {
          if (filter?.Operator === ("IN" as FilterOperator)) {
            const f: QueryFilterSimple = JSON.parse(JSON.stringify(filter));
            const firstVal: string | number | boolean = f.Value[0];
            if (firstVal === true || firstVal === false || firstVal === null) {
              f.Operator = "IS" as FilterOperator;
            } else {
              f.Operator = "=" as FilterOperator;
            }
            f.Value = firstVal;
            return f;
          } else if (filter?.Operator === ("NOT IN" as FilterOperator)) {
            const f: QueryFilterSimple = JSON.parse(JSON.stringify(filter));
            const firstVal: string | number | boolean = f.Value[0];
            if (firstVal === true || firstVal === false || firstVal === null) {
              f.Operator = "IS NOT" as FilterOperator;
            } else {
              f.Operator = "!=" as FilterOperator;
            }
            f.Value = firstVal;
            return f;
          }
        } else if ((filter?.Value?.length ?? 0) > 1) {
          const valuesThatNeedIs: FilterValue[] = (filter?.Value ?? []).filter(
            (v) => v === null || v === true || v === false,
          );
          if (valuesThatNeedIs.length > 0) {
            return {
              FiltersJoinWith: "OR",
              Filters: valuesThatNeedIs
                .map((v) => this.simplifyFilter({ ...filter, Value: [v] }))
                .concat([
                  this.simplifyFilter({
                    ...filter,
                    Value: filter.Value.filter((v) => v !== null && v !== true && v !== false),
                  }),
                ]),
            } as FilterableL1;
          }
        }
      }
    } catch (e) {
      this.handleSyncError("simplifyFilter", e);
    }
    return filter;
  }

  private scoringHelper(t: string, v: QueryFilterSimple["Value"]): number {
    if (typeof v === "string") {
      if (t === FiltersType.date || t === FiltersType.timestamp) {
        return 2;
      }
      return v.length;
    }
    if (Array.isArray(v)) {
      return v.map((k) => this.scoringHelper(t, k)).reduce((a, b) => a + b, 0);
    }
    return 1;
  }
  public sortFilters<T extends FilterableL1 | FilterableL2>(payload: T): T {
    const { Filters, ...rest } = payload;
    if ((Filters?.length ?? 0) > 0) {
      for (let i = 0; i < (payload?.Filters?.length ?? 0); i++) {
        if (((Filters[i] as FilterableL2)?.Filters?.length ?? 0) > 0) {
          Filters[i] = this.sortFilters(Filters[i] as FilterableL1) as FilterableL2;
        }
      }
      // @ts-ignore
      return {
        ...rest,
        Filters: (Filters ?? []).sort((a, b): number => {
          const isANested: boolean = ((a as FilterableL2)?.Filters ?? [])?.length > 0;
          const isBNested: boolean = ((b as FilterableL2)?.Filters ?? [])?.length > 0;
          if (isANested && isBNested) {
            return ((a as FilterableL2)?.Filters ?? [])?.length >= ((b as FilterableL2)?.Filters ?? [])?.length
              ? -1
              : 1;
          } else if (isBNested) {
            return -1;
          } else if (isANested) {
            return 1;
          } else {
            return ((a as QueryFilterSimple)?.Attribute ?? "").localeCompare((b as QueryFilterSimple)?.Attribute ?? "");
          }
        }) as (FilterableL2 | QueryFilterSimple)[],
      };
    }
    return payload;
  }
  // DO NOT USE
  public updateVCloudFilters(
    currentFilters: QueryFilterSimple[],
    newFilters: QueryFilterSimple[],
    startDate?: Date,
    endDate?: Date,
  ): QueryFilterSimple[] {
    const Filters: QueryFilterSimple[] = [];
    if (currentFilters[0]) Filters.push(currentFilters[0]);
    if (currentFilters[1]) Filters.push(currentFilters[1]);
    if (newFilters.length > 0) Filters.push(...newFilters);
    if (startDate) {
      if (Filters[0].Type === FiltersType.date) {
        Filters[0].Value = startDate.toISOString().split("T")[0];
      } else {
        Filters[0].Value = startDate.toISOString();
      }
    }
    if (endDate) {
      if (Filters[1].Type === FiltersType.date) {
        Filters[1].Value = endDate.toISOString().split("T")[0];
      } else {
        Filters[1].Value = endDate.toISOString();
      }
    }
    return Filters;
  }

  public orderFilters(filters: QueryFilterSimple[]): QueryFilterSimple[] {
    const scores: { [attr: string]: number } = {};
    for (const f of filters) {
      scores[f.Table ? `${f.Table}.${f.Attribute}` : f.Attribute] =
        (OPERATOR_SCORES[f.Operator] ?? 1) * this.scoringHelper(f.Type, f.Value);
    }
    this.log.info({ msg: "filter scores", scores });
    return filters.slice().sort((a, b) => {
      const s1: number = scores[a.Table ? `${a.Table}.${a.Attribute}` : a.Attribute];
      const s2: number = scores[b.Table ? `${b.Table}.${b.Attribute}` : b.Attribute];
      if (s1 > s2) {
        return 1;
      } else if (s1 === s2) {
        return 0;
      }
      return -1;
    });
  }
}
